// src/rms/pages/AdvertiserPortal.js
// ============================================================================
// Advertiser self-serve portal — /advertiser
// ============================================================================
// Where a marine insurer signs in, builds a campaign, and checks whether it is
// working. Strictly scoped to the signed-in advertiser: their campaigns, their
// creatives, their numbers, and nothing else.
//
// AUTH NOTE. With the local backend there is no real authentication, so signing
// in here means selecting an advertiser record — clearly labelled as a
// development shortcut. On Supabase this becomes a real session, and the
// isolation is enforced by RLS (rms_is_advertiser_member) rather than by this
// screen. The UI restrictions below are convenience; the database is the
// boundary.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import db, { isLocalBackend } from "../data";
import campaignService, { listCampaignsWithCreatives, campaignPerformance } from "../ads/campaignService";
import CampaignForm, { blankCampaign, toCampaignDraft } from "../components/CampaignForm";
import { pacingStatus, ineligibleReason } from "../ads/campaignSource";
import { VERTICAL_PRESETS } from "../verticals/presets";
import { PageHeader, Stat, StatusPill, Banner, EmptyState, Field, Select, formatDate } from "../components/ui";
import "../styles/rms.css";
import { getLocale } from "../i18n/locale";
import Brandmark from "../components/Brandmark";

const SESSION_KEY = "rms.advertiser.session";
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

function AdvertiserPortal() {
  const { tab = "campaigns" } = useParams();
  const navigate = useNavigate();

  const [advertiserId, setAdvertiserId] = useState(() => {
    try {
      return window.sessionStorage.getItem(SESSION_KEY) || "";
    } catch {
      return "";
    }
  });
  const [advertiser, setAdvertiser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!advertiserId) {
      setLoading(false);
      return;
    }
    const record = (await db.get("advertisers", advertiserId)).data;
    setAdvertiser(record);
    setCampaigns(await listCampaignsWithCreatives(advertiserId));
    setLoading(false);
  }, [advertiserId]);

  useEffect(() => {
    load();
  }, [load]);

  const signIn = (id) => {
    try {
      window.sessionStorage.setItem(SESSION_KEY, id);
    } catch {
      /* session-only */
    }
    setAdvertiserId(id);
    setLoading(true);
  };

  const signOut = () => {
    try {
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* nothing to clear */
    }
    setAdvertiserId("");
    setAdvertiser(null);
    setCampaigns([]);
  };

  const save = async (draft) => {
    const result = await campaignService.saveCampaign(
      { ...draft, advertiser_id: advertiserId },
      { role: "advertiser" }
    );
    if (!result.success) {
      setNotice({ tone: "danger", text: result.error });
      return;
    }
    setEditing(null);
    setNotice({ tone: "ok", text: "Submitted. Our team reviews new creative within one working day." });
    load();
  };

  if (!advertiserId) return <SignIn onSignIn={signIn} />;
  if (loading) return <Shell advertiser={null} onSignOut={signOut}><p style={{ color: "var(--rms-muted-strong)" }}>Loading…</p></Shell>;
  if (!advertiser) {
    return (
      <Shell advertiser={null} onSignOut={signOut}>
        <Banner tone="danger">That advertiser account no longer exists.</Banner>
      </Shell>
    );
  }

  return (
    <Shell advertiser={advertiser} onSignOut={signOut}>
      {notice ? <Banner tone={notice.tone}>{notice.text}</Banner> : null}

      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {[{ key: "campaigns", label: "Campaigns" }, { key: "performance", label: "Performance" }].map((t) => (
          <button
            key={t.key}
            type="button"
            className={`rms-btn ${tab === t.key ? "rms-btn--primary" : "rms-btn--ghost"} rms-btn--sm`}
            onClick={() => navigate(`/advertiser/${t.key}`)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "performance" ? (
        <PerformanceTab campaigns={campaigns} />
      ) : (
        <CampaignsTab
          campaigns={campaigns}
          onNew={() => setEditing(blankCampaign(advertiserId))}
          onEdit={(c) => setEditing(toCampaignDraft(c, c.creatives))}
        />
      )}

      {editing ? (
        <CampaignForm draft={editing} mode="advertiser" onCancel={() => setEditing(null)} onSave={save} />
      ) : null}
    </Shell>
  );
}

// ----------------------------------------------------------------------------

function Shell({ advertiser, onSignOut, children }) {
  return (
    <div className="rms-root" style={{ minHeight: "100vh" }}>
      <header className="rms-topbar rms-topbar--global">
        <div className="rms-topbar-identity">
          <div className="rms-topbar-business">{advertiser ? advertiser.name : "Advertiser portal"}</div>
          <div className="rms-topbar-vertical">ReserveZoo advertising</div>
        </div>
        <div className="rms-topbar-meta">
          <Link className="rms-btn rms-btn--ghost rms-btn--sm" to="/advertise">Media kit</Link>
          {advertiser ? (
            <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={onSignOut}>
              Sign out
            </button>
          ) : null}
        </div>
      </header>
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "24px 24px 64px" }}>{children}</div>
    </div>
  );
}

function SignIn({ onSignIn }) {
  const [advertisers, setAdvertisers] = useState([]);
  const [choice, setChoice] = useState("");

  useEffect(() => {
    db.list("advertisers", {}, { orderBy: "name" }).then((r) => setAdvertisers(r.data || []));
  }, []);

  return (
    <div className="rms-root rms-landing" style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 460, margin: "0 auto", padding: "80px 24px" }}>
        <Brandmark style={{ padding: "0 0 24px" }} />

        <h1 className="rms-page-title" style={{ fontSize: 24 }}>Advertiser sign in</h1>
        <p className="rms-page-sub" style={{ marginBottom: 20 }}>
          Manage your campaigns and see how they're performing.
        </p>

        <div className="rms-card rms-card-pad">
          {isLocalBackend ? (
            <Banner tone="info">
              <div>
                <strong>Development mode.</strong> There's no real authentication on the local
                backend, so pick an account to continue. With a live backend this becomes an email
                and password, and access is enforced by the database rather than this screen.
              </div>
            </Banner>
          ) : null}

          {advertisers.length === 0 ? (
            <div>
              <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)" }}>
                No advertiser accounts exist yet. Create one in the sales console first.
              </p>
              <Link className="rms-btn rms-btn--ghost rms-btn--sm" to="/ad-sales/advertisers">
                Open sales console
              </Link>
            </div>
          ) : (
            <>
              <Field label="Account" required>
                <Select
                  value={choice}
                  onChange={setChoice}
                  placeholder="Choose your company…"
                  options={advertisers.map((a) => ({ value: a.id, label: a.name }))}
                />
              </Field>
              <button
                type="button"
                className="rms-btn rms-btn--primary"
                style={{ width: "100%" }}
                disabled={!choice}
                onClick={() => onSignIn(choice)}
              >
                Sign in
              </button>
            </>
          )}
        </div>

        <p className="rms-help" style={{ textAlign: "center", marginTop: 16 }}>
          Not advertising with us yet? <Link to="/advertise">See what's available →</Link>
        </p>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------

function CampaignsTab({ campaigns, onNew, onEdit }) {
  const labelOf = VERTICAL_PRESETS.reduce((acc, p) => ({ ...acc, [p.adCategory]: p.label }), {});

  return (
    <>
      <PageHeader
        title="Your campaigns"
        subtitle={`${campaigns.length} campaign${campaigns.length === 1 ? "" : "s"}`}
        actions={
          <button type="button" className="rms-btn rms-btn--primary" onClick={onNew}>
            New campaign
          </button>
        }
      />

      {campaigns.length === 0 ? (
        <EmptyState
          title="No campaigns yet"
          message="Build one and our team will review it — usually within a working day."
          action={
            <button type="button" className="rms-btn rms-btn--primary" onClick={onNew}>
              New campaign
            </button>
          }
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Targeting</th>
                <th>Flight</th>
                <th>Creatives</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const pending = c.creatives.filter((cr) => cr.review_status === "pending").length;
                const rejected = c.creatives.filter((cr) => cr.review_status === "rejected").length;
                const approved = c.creatives.filter((cr) => cr.review_status === "approved").length;
                const blocked = c.status === "live" ? ineligibleReason(c) : null;

                return (
                  <tr key={c.id}>
                    <td><strong>{c.name}</strong></td>
                    <td style={{ fontSize: 12.5 }}>
                      {(c.ad_categories || []).map((k) => labelOf[k] || k).join(", ")}
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      {formatDate(c.starts_at)} → {formatDate(c.ends_at)}
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      {approved ? <StatusPill status="active" label={`${approved} live`} /> : null}
                      {pending ? <StatusPill status="pending" label={`${pending} in review`} /> : null}
                      {rejected ? <StatusPill status="cancelled" label={`${rejected} rejected`} /> : null}
                    </td>
                    <td>
                      <StatusPill
                        status={c.status === "live" ? "active" : c.status === "rejected" ? "cancelled" : "pending"}
                        label={c.status.replace(/_/g, " ")}
                      />
                      {blocked ? (
                        <div style={{ fontSize: 11, color: "var(--rms-warn)", marginTop: 3 }}>{blocked}</div>
                      ) : null}
                    </td>
                    <td className="rms-num">
                      <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => onEdit(c)}>
                        {["draft", "pending_approval", "rejected"].includes(c.status) ? "Edit" : "View"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ----------------------------------------------------------------------------

function PerformanceTab({ campaigns }) {
  const [stats, setStats] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = {};
      for (const c of campaigns) {
        // eslint-disable-next-line no-await-in-loop
        out[c.id] = await campaignPerformance(c.id);
      }
      if (!cancelled) setStats(out);
    })();
    return () => {
      cancelled = true;
    };
  }, [campaigns]);

  const totals = useMemo(
    () =>
      Object.values(stats).reduce(
        (acc, s) => ({
          impressions: acc.impressions + s.impressions,
          clicks: acc.clicks + s.clicks,
          spend: acc.spend + s.spendCents,
        }),
        { impressions: 0, clicks: 0, spend: 0 }
      ),
    [stats]
  );

  return (
    <>
      <PageHeader
        title="Performance"
        subtitle="Viewable impressions only — counted once 50% of the ad has been on screen for a full second."
      />

      <div className="rms-stat-row">
        <Stat label="Viewable impressions" value={totals.impressions.toLocaleString(getLocale())} />
        <Stat label="Clicks" value={totals.clicks.toLocaleString(getLocale())} />
        <Stat
          label="Click rate"
          value={totals.impressions ? `${((totals.clicks / totals.impressions) * 100).toFixed(2)}%` : "—"}
        />
        <Stat label="Spend" value={money(totals.spend)} />
      </div>

      {totals.impressions === 0 ? (
        <Banner tone="info">
          Nothing delivered yet. Numbers appear here once a campaign is approved and starts serving.
        </Banner>
      ) : null}

      {campaigns.length === 0 ? (
        <EmptyState title="No campaigns" message="Create a campaign to see performance here." />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th className="rms-num">Viewable</th>
                <th className="rms-num">Clicks</th>
                <th className="rms-num">CTR</th>
                <th className="rms-num">Spend</th>
                <th className="rms-num">Effective CPM</th>
                <th>Pacing</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const s = stats[c.id] || { impressions: 0, clicks: 0, ctr: 0, spendCents: 0, ecpmCents: 0 };
                const pace = pacingStatus(c);
                return (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.name}</strong>
                      <div style={{ fontSize: 12, color: "var(--rms-muted)" }}>
                        {formatDate(c.starts_at)} → {formatDate(c.ends_at)}
                      </div>
                    </td>
                    <td className="rms-num">{s.impressions.toLocaleString(getLocale())}</td>
                    <td className="rms-num">{s.clicks.toLocaleString(getLocale())}</td>
                    <td className="rms-num">{s.ctr.toFixed(2)}%</td>
                    <td className="rms-num">{money(s.spendCents)}</td>
                    <td className="rms-num">{money(s.ecpmCents)}</td>
                    <td>
                      {pace ? (
                        <>
                          <StatusPill
                            status={pace.state === "on_pace" ? "active" : pace.state === "under" ? "overdue" : "pending"}
                            label={pace.state.replace(/_/g, " ")}
                          />
                          <div style={{ fontSize: 11, color: "var(--rms-muted)", marginTop: 3 }}>
                            {Math.round(pace.deliveredPct)}% delivered · {Math.round(pace.timeElapsedPct)}% elapsed
                          </div>
                        </>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default AdvertiserPortal;
